/**
 * EngagementModal — patched
 * FIX 1:  normalizeComponents dedup key includes product_service
 * FIX 2:  ActualsSection reads rawData?.components
 * FIX 3:  ActualsDetailModal revenue from monthly_revenue; Month → Service → Employee
 * FIX 4:  normalizeComponents filters on hours > 0 (not cost > 0)
 * FIX 5:  Year filter in Settings sub-modal — propagates to Pricing + Actuals + Detail
 * FIX 6:  QB section removed — implicit via engagement ID
 * FIX 7:  Profit Margin tile in PricingSection
 * FIX 8:  Extract & Save moved into Settings sub-modal
 * FIX 9:  Service category grouping — standard list + "Other" bucket
 * FIX 10: handleApplyAndSave receives fully merged formData (stale closure fix)
 * FIX 11: Swap custom modal shell for common Modal component
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowPathIcon, CheckCircleIcon, ChevronDownIcon, ChevronRightIcon,
  ClipboardDocumentIcon, Cog6ToothIcon, DocumentTextIcon, InformationCircleIcon,
  CurrencyDollarIcon, PresentationChartBarIcon, XMarkIcon,
  UserGroupIcon, ArrowsPointingOutIcon, LightBulbIcon, EyeIcon, SparklesIcon,DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import Modal from '../../common/Modal';
import ExtractionPanel from './ExtractionPanel';
import { useSyncActuals, useUpdateEngagement } from '../../../hooks/engagement/useEngagement';
import { useQueryClient } from '@tanstack/react-query';
import { useBidEngagementComponents } from '../../../hooks/bid_wizard/useBidEngagements';
import { useUploadDocument, useExtractProposal } from '../../../hooks/document/useDocument';

// ── Standard service categories ───────────────────────────────────────────────
// Edit this list to control which services get their own panel.
// Anything NOT in this list is bucketed into OTHER_BUCKET_LABEL.
// Matching is case-insensitive.

const STANDARD_SERVICE_CATEGORIES = [
  'Strategic Marketing',
  'Media',
  'Nominations',
  'Speaking',
  'Website',
  'Social Media',
  'Newsletters',
  'Advertising',
  'Marketing Materials',
  'Search Engine Optimize',
];
const OTHER_BUCKET_LABEL = 'Other';

const normalizeServiceName = (raw) => {
  const trimmed = (raw || '').trim();
  const match = STANDARD_SERVICE_CATEGORIES.find(
    (s) => s.toLowerCase() === trimmed.toLowerCase()
  );
  return match || OTHER_BUCKET_LABEL;
};

// ── Config ────────────────────────────────────────────────────────────────────
const ROLE_DISPLAY_NAMES = {
  'Sr AM': 'Sr. Account Manager', 'Acct Mgr': 'Account Manager', 'AM': 'Account Manager',
  'AAM': 'Assistant Account Manager', 'Asst Acct Mgr': 'Assistant Account Manager',
  'Asst Account Mgr': 'Assistant Account Manager', 'Marketing Support': 'Marketing Support',
  'Principal': 'Principal', 'CEO': 'Principal', 'Freelance': 'Freelance', 'Team Member': 'Freelance',
};

const TOOLTIPS = {
  average: 'Average hours worked per month for this role, calculated across all months in the engagement period.',
  range:   'The lowest (Min) to highest (Max) hours recorded in any single month for this role.',
  margin:  'Profit margin = (Revenue − Cost) ÷ Revenue × 100.',
};

const SECTIONS = [
  { id: 'overview',  label: 'Overview',  Icon: InformationCircleIcon },
  { id: 'pricing',   label: 'Pricing',   Icon: CurrencyDollarIcon },
  { id: 'document',  label: 'Document',  Icon: DocumentTextIcon },
  { id: 'actuals',   label: 'Actuals',   Icon: PresentationChartBarIcon },
];

export const ENGAGEMENT_TYPES   = ['Project', 'Retainer', 'Consulting', 'Advisory'];
export const BID_STATUSES       = ['Pending', 'Won', 'Lost', 'Declined'];
export const DEFAULT_INDUSTRIES = ['Technology','Healthcare','Finance','Legal','Manufacturing','Retail','Education','Other'];

// ── Form helpers ──────────────────────────────────────────────────────────────

export const getEmptyFormData = (overrides = {}) => ({
  engagementName: '', engagementType: 'Project', industry: '', practiceArea: '',
  referralSource: '', scopeSummary: '', tags: [], qbCustomerRef: '', qbCustomerName: '',
  bidStatus: 'Pending', bidDate: '', decisionDate: '', startDate: '', endDate: '',
  bidAmount: '', competitors: [], winLossReason: '', lessonsLearned: '', documentId: '',
  ...overrides,
});

export const engagementToFormData = (e) => ({
  engagementName: e.engagement_name || '', engagementType: e.engagement_type || 'Project',
  industry: e.industry || '', practiceArea: e.practice_area || '',
  referralSource: e.referral_source || '', scopeSummary: e.scope_summary || '',
  tags: e.tags || [], qbCustomerRef: e.qb_customer_ref || '', qbCustomerName: e.qb_customer_name || '',
  bidStatus: e.bid_status || 'Pending', bidDate: e.bid_date || '',
  decisionDate: e.decision_date || '', startDate: e.start_date || '', endDate: e.end_date || '',
  bidAmount: e.bid_amount || '', competitors: e.competitors || [],
  winLossReason: e.win_loss_reason || '', lessonsLearned: e.lessons_learned || '',
  documentId: e.document_id || '',
});

export const formDataToPayload = (f) => ({
  engagement_name: f.engagementName || undefined, engagement_type: f.engagementType || undefined,
  industry: f.industry || undefined, practice_area: f.practiceArea || undefined,
  referral_source: f.referralSource || undefined, scope_summary: f.scopeSummary || undefined,
  tags: f.tags ?? [], qb_customer_ref: f.qbCustomerRef || undefined,
  qb_customer_name: f.qbCustomerName || undefined, bid_status: f.bidStatus || undefined,
  bid_date: f.bidDate || undefined, decision_date: f.decisionDate || undefined,
  start_date: f.startDate || undefined, end_date: f.endDate || undefined,
  bid_amount: f.bidAmount !== '' ? parseFloat(f.bidAmount) : undefined,
  competitors: f.competitors ?? [], win_loss_reason: f.winLossReason || undefined,
  lessons_learned: f.lessonsLearned || undefined,
});

// ── Utilities ─────────────────────────────────────────────────────────────────

const fmt = (val) =>
  val != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val) : null;

const fmtHrs = (val) => val != null ? `${Number(val).toFixed(1)} hrs` : '—';
const getRoleDisplayName = (role) => ROLE_DISPLAY_NAMES[role] ?? role;

const normalizeComponents = (rawComponents) => {
  if (!rawComponents?.length) return [];
  const seen = {};
  rawComponents.forEach((c) => {
    const key = `${c.month}-${c.employee_name}-${c.product_service || ''}`;
    if (!seen[key] || (c.created_at || '') > (seen[key].created_at || '')) seen[key] = c;
  });
  return Object.values(seen).filter((c) => (c.hours || 0) > 0);
};

const applyYearFilter = (items, selectedYear, monthKey = 'month') => {
  if (!selectedYear || selectedYear === 'all') return items;
  return items.filter((item) => (item[monthKey] || '').slice(0, 4) >= selectedYear);
};

const buildAnalysis = (components, isRetainer) => {
  if (!components?.length) return null;
  const months     = [...new Set(components.map((c) => c.month))].sort();
  const monthCount = months.length;
  const totalHours = components.reduce((s, c) => s + (c.hours || 0), 0);
  const totalCost  = components.reduce((s, c) => s + (c.cost  || 0), 0);

  const srMap = {};
  components.forEach((c) => {
    const svc  = normalizeServiceName(c.product_service);
    const role = c.employee_role || 'Unknown';
    const key  = `${svc}|||${role}`;
    if (!srMap[key]) srMap[key] = { service: svc, role, hours: 0, cost: 0, employees: new Set(), monthlyHours: {} };
    srMap[key].hours += c.hours || 0;
    srMap[key].cost  += c.cost  || 0;
    if (c.employee_name) srMap[key].employees.add(c.employee_name);
    srMap[key].monthlyHours[c.month] = (srMap[key].monthlyHours[c.month] || 0) + (c.hours || 0);
  });

  const allRows = Object.values(srMap).map((r) => {
    const vals = months.map((m) => r.monthlyHours[m] || 0);
    return {
      service: r.service, role: r.role, totalHours: r.hours,
      avgHours: isRetainer ? r.hours / monthCount : r.hours,
      minHours: vals.length ? Math.min(...vals) : 0,
      maxHours: vals.length ? Math.max(...vals) : 0,
      employeeNames: [...r.employees], employeeCount: r.employees.size,
    };
  });

  const svcMap = {};
  allRows.forEach((r) => {
    if (!svcMap[r.service]) svcMap[r.service] = { service: r.service, roles: [], totalHours: 0 };
    svcMap[r.service].roles.push(r);
    svcMap[r.service].totalHours += r.totalHours;
  });
  
  Object.values(svcMap).forEach((sg) => {
    sg.roles.sort((a, b) => b.totalHours - a.totalHours);
    // For retainers, totalHours on the service group = sum of avgHours across roles
    // so the header shows monthly total, not all-time total
    if (isRetainer) {
      sg.totalHours = sg.roles.reduce((s, r) => s + r.avgHours, 0);
    }
  });

  const services = Object.values(svcMap).sort((a, b) => {
    if (a.service === OTHER_BUCKET_LABEL) return 1;
    if (b.service === OTHER_BUCKET_LABEL) return -1;
    const ai = STANDARD_SERVICE_CATEGORIES.indexOf(a.service);
    const bi = STANDARD_SERVICE_CATEGORIES.indexOf(b.service);
    if (ai === -1 && bi === -1) return a.service.localeCompare(b.service);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const monthlyBreakdown = months.map((month) => {
    const mc = components.filter((c) => c.month === month);
    const serviceMap = {};
    mc.forEach((c) => {
      const svc = normalizeServiceName(c.product_service);
      if (!serviceMap[svc]) serviceMap[svc] = { hours: 0, cost: 0, employees: [] };
      serviceMap[svc].hours += c.hours || 0;
      serviceMap[svc].cost  += c.cost  || 0;
      serviceMap[svc].employees.push(c);
    });
    Object.values(serviceMap).forEach((s) => s.employees.sort((a, b) => (b.hours || 0) - (a.hours || 0)));
    const monthServices = Object.entries(serviceMap)
      .sort(([a], [b]) => {
        if (a === OTHER_BUCKET_LABEL) return 1;
        if (b === OTHER_BUCKET_LABEL) return -1;
        return STANDARD_SERVICE_CATEGORIES.indexOf(a) - STANDARD_SERVICE_CATEGORIES.indexOf(b);
      })
      .map(([name, data]) => ({ name, ...data }));
    return {
      month,
      hours: mc.reduce((s, c) => s + (c.hours || 0), 0),
      cost:  mc.reduce((s, c) => s + (c.cost  || 0), 0),
      services: monthServices,
    };
  }).reverse();

  return { monthCount, totalHours, totalCost, services, monthlyBreakdown, isRetainer };
};

// ── Primitives ────────────────────────────────────────────────────────────────

const Tooltip = ({ text, direction = 'up' }) => {
  const [visible, setVisible] = useState(false);
  const isUp = direction === 'up';
  return (
    <span className="relative inline-flex items-center ml-1">
      <button type="button"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="text-grey-300 hover:text-grey-500 transition-colors focus:outline-none">
        <InformationCircleIcon className="w-3.5 h-3.5" />
      </button>
      {visible && (
        <div className={`absolute ${isUp ? 'bottom-full mb-2' : 'top-full mt-2'} left-1/2 -translate-x-1/2 z-[80] w-56 px-3 py-2 bg-darkblue-600 text-white text-xs rounded-lg shadow-lg leading-snug pointer-events-none`}>
          {text}
          <div className={`absolute ${isUp ? 'top-full border-t-darkblue-600' : 'bottom-full border-b-darkblue-600'} left-1/2 -translate-x-1/2 border-4 border-transparent`} />
        </div>
      )}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = { Won: 'bg-teal-500 text-white', Lost: 'bg-pink-500 text-white', Pending: 'bg-yellow-500 text-white', Declined: 'bg-grey-500 text-white' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || styles.Pending}`}>{status}</span>;
};

const EngagementIdChip = ({ id }) => {
  const [copied, setCopied] = useState(false);
  if (!id) return null;
  return (
    <button type="button" onClick={() => { navigator.clipboard.writeText(String(id)).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
      title="Click to copy" className="inline-flex items-center gap-1 px-2 py-0.5 bg-darkblue-50 border border-darkblue-200 text-darkblue-400 text-xs rounded-md hover:bg-darkblue-100 transition-colors">
      <span className="font-mono">#{id}</span>
      {copied ? <CheckCircleIcon className="w-3 h-3 text-teal-500" /> : <ClipboardDocumentIcon className="w-3 h-3" />}
    </button>
  );
};

const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold text-darkblue-500 mb-1">
    {children}{required && <span className="text-pink-500 ml-0.5">*</span>}
  </label>
);

const inputCls = (disabled) =>
  `w-full px-2.5 py-1.5 border rounded-lg text-sm outline-none transition-colors ${disabled ? 'bg-grey-100 border-grey-200 text-grey-400 cursor-not-allowed' : 'bg-white border-grey-300 text-darkblue-500 focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400'}`;

const SectionDivider = ({ title }) => (
  <div className="flex items-center gap-2 my-4">
    <div className="flex-1 border-t border-grey-200" />
    <span className="text-xs font-bold uppercase tracking-widest text-grey-400">{title}</span>
    <div className="flex-1 border-t border-grey-200" />
  </div>
);

const CollapseSection = ({ label, icon: Icon, defaultOpen = false, badge, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-grey-50 hover:bg-grey-100 transition-colors text-left">
        <span className="flex items-center gap-2 text-xs font-semibold text-darkblue-500">
          {Icon && <Icon className="w-3.5 h-3.5 text-grey-400" />}{label}
          {badge && <span className="px-1.5 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium">{badge}</span>}
        </span>
        {open ? <ChevronDownIcon className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />}
      </button>
      {open && <div className="px-3 py-3">{children}</div>}
    </div>
  );
};

const TagInput = ({ tags, onChange, disabled }) => {
  const [inputVal, setInputVal] = useState('');
  const addTag = (raw) => { const t = raw.trim().toLowerCase().replace(/\s+/g, '-'); if (!t || tags.includes(t)) return; onChange([...tags, t]); };
  const removeTag = (tag) => onChange(tags.filter((t) => t !== tag));
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(inputVal); setInputVal(''); }
    else if (e.key === 'Backspace' && !inputVal && tags.length) removeTag(tags[tags.length - 1]);
  };
  return (
    <div className={`flex flex-wrap gap-1 px-2.5 py-1.5 min-h-[34px] border rounded-lg transition-colors ${disabled ? 'bg-grey-100 border-grey-200' : 'bg-white border-grey-300 focus-within:ring-2 focus-within:ring-brightblue-300 focus-within:border-brightblue-400'}`}>
      {tags.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-darkblue-50 text-darkblue-600 border border-darkblue-200 text-xs font-medium rounded-full">
          {tag}{!disabled && <button type="button" onClick={() => removeTag(tag)} className="text-darkblue-400 hover:text-darkblue-700 leading-none">×</button>}
        </span>
      ))}
      {!disabled && <input type="text" value={inputVal} onChange={(e) => setInputVal(e.target.value.replace(',', ''))}
        onKeyDown={handleKeyDown} onBlur={() => { if (inputVal.trim()) { addTag(inputVal); setInputVal(''); } }}
        placeholder={tags.length === 0 ? 'Add tags...' : ''} className="flex-1 min-w-[80px] text-xs text-darkblue-500 outline-none bg-transparent placeholder:text-grey-300" />}
    </div>
  );
};

const PricingTile = ({ label, value, sub, color = 'default', tooltip }) => {
  const bg = { default: 'bg-grey-50 border-grey-200', teal: 'bg-teal-50 border-teal-200', darkblue: 'bg-darkblue-50 border-darkblue-200', brightblue: 'bg-brightblue-50 border-brightblue-200', yellow: 'bg-yellow-50 border-yellow-200', red: 'bg-pink-50 border-pink-200' };
  const vc = { default: 'text-darkblue-500', teal: 'text-teal-700', darkblue: 'text-darkblue-600', brightblue: 'text-brightblue-700', yellow: 'text-yellow-700', red: 'text-pink-700' };
  return (
    <div className={`rounded-lg border p-2.5 ${bg[color]}`}>
      <p className="text-xs text-grey-500 uppercase tracking-wide font-medium mb-1 flex items-center">{label}{tooltip && <Tooltip text={tooltip} />}</p>
      <p className={`text-base font-bold ${vc[color]}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-grey-400 mt-0.5">{sub}</p>}
    </div>
  );
};

const YearFilterPills = ({ selectedYear, onChange, availableYears }) => (
  <div className="flex items-center gap-0.5 bg-grey-100 rounded-lg p-0.5 flex-wrap">
    <button type="button" onClick={() => onChange('all')}
      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${selectedYear === 'all' ? 'bg-white text-darkblue-500 shadow-sm' : 'text-grey-500 hover:text-darkblue-500'}`}>
      All time
    </button>
    {availableYears.map((yr) => (
      <button key={yr} type="button" onClick={() => onChange(yr)}
        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${selectedYear === yr ? 'bg-white text-darkblue-500 shadow-sm' : 'text-grey-500 hover:text-darkblue-500'}`}>
        {yr}+
      </button>
    ))}
  </div>
);

// ── Settings sub-modal ────────────────────────────────────────────────────────

const SettingsModal = ({
  isOpen, onClose,
  selectedYear, onYearChange, availableYears,
  formData, setFormData, isEdit, onApplyAndSave, documents,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const linkedDoc = documents.find((d) => d.id === formData.documentId);

  // FIX 10: merge in SettingsModal where formData is current
  const handleExtractionApply = useCallback(async (updates) => {
    const merged = { ...formData, ...updates };
    setFormData(merged);
    if (isEdit && onApplyAndSave) {
      setIsSaving(true);
      try { await onApplyAndSave(merged); }
      catch (e) { console.error('Auto-save after extraction failed:', e); }
      finally { setIsSaving(false); }
    }
  }, [formData, isEdit, onApplyAndSave, setFormData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-grey-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="w-4 h-4 text-darkblue-400" />
            <h3 className="text-sm font-bold text-darkblue-500">Engagement Settings</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Actuals Period */}
          <div>
            <p className="text-xs font-bold text-darkblue-500 mb-0.5">Actuals Period</p>
            <p className="text-xs text-grey-400 mb-2.5">
              Filters revenue, cost, hours and margin in the Pricing and Actuals sections.
            </p>
            <YearFilterPills selectedYear={selectedYear} onChange={onYearChange} availableYears={availableYears} />
          </div>

          <div className="border-t border-grey-100" />

          {/* Document Extraction */}
          <div>
            <p className="text-xs font-bold text-darkblue-500 mb-0.5">Document Extraction</p>
            <p className="text-xs text-grey-400 mb-2.5">
              Extract fields from the linked proposal document and apply them to this engagement.
              {isEdit && ' Changes are saved automatically.'}
            </p>
            {!linkedDoc ? (
              <div className="flex items-center gap-2 p-3 bg-grey-50 border border-grey-200 rounded-lg">
                <DocumentTextIcon className="w-4 h-4 text-grey-300" />
                <p className="text-xs text-grey-400">No document linked. Link a document in the Document section first.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2.5 bg-darkblue-50 border border-darkblue-200 rounded-lg">
                  <DocumentTextIcon className="w-4 h-4 text-darkblue-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-darkblue-500 flex-1 truncate">{linkedDoc.display_name}</span>
                  {isSaving && (
                    <span className="text-xs text-teal-600 flex items-center gap-1 flex-shrink-0">
                      <ArrowPathIcon className="w-3 h-3 animate-spin" />Saving...
                    </span>
                  )}
                </div>
                {isEdit && (
                  <p className="text-xs text-teal-600 flex items-center gap-1 px-0.5">
                    <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    Fields will be saved automatically when you click "Apply to Form"
                  </p>
                )}
                <ExtractionPanel
                  documentId={formData.documentId}
                  formData={formData}
                  onApply={handleExtractionApply}
                />
              </div>
            )}
          </div>

          <div className="border-t border-grey-100" />

          {/* Service Categories — read-only info */}
          <div>
            <p className="text-xs font-bold text-darkblue-500 mb-0.5">Service Categories</p>
            <p className="text-xs text-grey-400 mb-2.5">
              Standard categories shown as separate panels in the Actuals section. Everything else is grouped under "{OTHER_BUCKET_LABEL}".
              Edit <code className="bg-grey-100 px-1 rounded text-darkblue-400 text-xs">STANDARD_SERVICE_CATEGORIES</code> in the source file to customise.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STANDARD_SERVICE_CATEGORIES.map((s) => (
                <span key={s} className="px-2 py-0.5 bg-white border border-grey-300 text-darkblue-500 text-xs rounded-full">{s}</span>
              ))}
              <span className="px-2 py-0.5 bg-grey-100 border border-grey-200 text-grey-500 text-xs rounded-full">+ {OTHER_BUCKET_LABEL}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-grey-200 flex-shrink-0 bg-grey-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Scrollspy nav ─────────────────────────────────────────────────────────────

const ScrollspyNav = ({ sections, activeSection, onClickSection }) => (
  <div className="flex items-center gap-1 px-3 py-1.5 border-b border-grey-200 bg-grey-50 flex-shrink-0 overflow-x-auto scrollbar-hide">
    {sections.map(({ id, label }) => (
      <button key={id} type="button" onClick={() => onClickSection(id)}
        className={`flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200 whitespace-nowrap ${activeSection === id ? 'bg-darkblue-500 text-white shadow-sm' : 'text-grey-500 hover:bg-grey-200 hover:text-darkblue-500'}`}>
        {label}
      </button>
    ))}
  </div>
);

// ── OverviewSection ───────────────────────────────────────────────────────────

const OverviewSection = ({ formData, updateField, isLoading, industries, engagementId }) => (
  <div className="space-y-3">
    <div>
      <Label required>Engagement Name</Label>
      <div className="flex items-center gap-2">
        <input type="text" value={formData.engagementName} onChange={(e) => updateField('engagementName', e.target.value)} disabled={isLoading} placeholder="e.g., Thompson Coburn – Monthly Retainer" className={`${inputCls(isLoading)} flex-1`} />
        <EngagementIdChip id={engagementId} />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <div><Label required>Status</Label><select value={formData.bidStatus} onChange={(e) => updateField('bidStatus', e.target.value)} disabled={isLoading} className={inputCls(isLoading)}>{BID_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
      <div><Label>Bid Date</Label><input type="date" value={formData.bidDate} onChange={(e) => updateField('bidDate', e.target.value)} disabled={isLoading} className={inputCls(isLoading)} /></div>
      <div><Label>Decision Date</Label><input type="date" value={formData.decisionDate} onChange={(e) => updateField('decisionDate', e.target.value)} disabled={isLoading} className={inputCls(isLoading)} /></div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <div><Label required>Type</Label><select value={formData.engagementType} onChange={(e) => updateField('engagementType', e.target.value)} disabled={isLoading} className={inputCls(isLoading)}>{ENGAGEMENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
      <div><Label>Industry</Label><select value={formData.industry} onChange={(e) => updateField('industry', e.target.value)} disabled={isLoading} className={inputCls(isLoading)}><option value="">Select...</option>{industries.map((i) => <option key={i}>{i}</option>)}</select></div>
      <div><Label>Practice Area</Label><input type="text" value={formData.practiceArea} onChange={(e) => updateField('practiceArea', e.target.value)} disabled={isLoading} placeholder="e.g., Litigation" className={inputCls(isLoading)} /></div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div><Label>Start Date</Label><input type="date" value={formData.startDate} onChange={(e) => updateField('startDate', e.target.value)} disabled={isLoading} className={inputCls(isLoading)} /></div>
      <div><Label>End Date</Label><input type="date" value={formData.endDate} onChange={(e) => updateField('endDate', e.target.value)} disabled={isLoading} className={inputCls(isLoading)} /></div>
    </div>
    <div><Label>Scope Summary</Label><textarea value={formData.scopeSummary} onChange={(e) => updateField('scopeSummary', e.target.value)} disabled={isLoading} rows={2} placeholder="Brief description..." className={inputCls(isLoading)} /></div>
    <div><Label>Tags</Label><TagInput tags={formData.tags || []} onChange={(tags) => updateField('tags', tags)} disabled={isLoading} /></div>
    <CollapseSection label="Intelligence & Notes" icon={LightBulbIcon}>
      <div className="space-y-3">
        <div><Label>Win / Loss Reason</Label><textarea value={formData.winLossReason} onChange={(e) => updateField('winLossReason', e.target.value)} disabled={isLoading} rows={2} placeholder="Why did we win or lose this bid?" className={inputCls(isLoading)} /></div>
        <div><Label>Lessons Learned</Label><textarea value={formData.lessonsLearned} onChange={(e) => updateField('lessonsLearned', e.target.value)} disabled={isLoading} rows={2} placeholder="Key takeaways..." className={inputCls(isLoading)} /></div>
      </div>
    </CollapseSection>
  </div>
);

// ── PricingSection ────────────────────────────────────────────────────────────

const PricingSection = ({ formData, existingEngagement, isLoading, updateField, selectedYear }) => {
  const engagementId = existingEngagement?.id;
  const isRetainer   = formData.engagementType?.toLowerCase() === 'retainer';
  const { data: rawData } = useBidEngagementComponents({ engagementId, enabled: !!engagementId });

  const allMonthlyRevenue = useMemo(() => rawData?.monthly_revenue || [], [rawData]);
  const allComponents     = useMemo(() => normalizeComponents(rawData?.components), [rawData]);
  const monthlyRevenue    = useMemo(() => applyYearFilter(allMonthlyRevenue, selectedYear), [allMonthlyRevenue, selectedYear]);
  const components        = useMemo(() => applyYearFilter(allComponents, selectedYear), [allComponents, selectedYear]);

  const totalRevenue     = useMemo(() => monthlyRevenue.reduce((s, r) => s + (r.revenue || 0), 0), [monthlyRevenue]);
  const billedMonthCount = useMemo(() => monthlyRevenue.filter((r) => (r.revenue || 0) > 0).length, [monthlyRevenue]);
  const avgRevenue       = billedMonthCount > 0 ? totalRevenue / billedMonthCount : null;
  const totalHours       = useMemo(() => components.reduce((s, c) => s + (c.hours || 0), 0), [components]);
  const totalCost        = useMemo(() => components.reduce((s, c) => s + (c.cost  || 0), 0), [components]);
  const profitMargin     = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : null;
  const marginColor      = profitMargin == null ? 'default' : profitMargin >= 20 ? 'teal' : profitMargin >= 10 ? 'yellow' : 'red';

  const bidAmount       = Number(formData.bidAmount) || 0;
  const comparisonBase  = isRetainer ? avgRevenue : totalRevenue;
  const showComparison  = bidAmount > 0 && comparisonBase != null && comparisonBase > 0;
  const comparisonDiff  = showComparison ? comparisonBase - bidAmount : 0;
  const comparisonPct   = showComparison ? ((comparisonBase / bidAmount) * 100).toFixed(0) : null;
  const comparisonAbove = comparisonDiff >= 0;
  const comparisonLabel  = isRetainer ? 'Avg monthly revenue' : 'Revenue';
  const comparisonSuffix = isRetainer ? ' (monthly)' : '';
  const periodLabel      = selectedYear && selectedYear !== 'all' ? `${selectedYear}+` : 'All time';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 p-3 bg-brightblue-50 border border-brightblue-200 rounded-lg">
        <div className="flex-shrink-0">
          <p className="text-xs font-semibold text-brightblue-700">{isRetainer ? 'Monthly Bid Amount' : 'Bid Amount'}</p>
          <p className="text-xs text-brightblue-500 mt-0.5">{isRetainer ? 'Monthly retainer quoted' : 'Total amount quoted'}</p>
        </div>
        <div className="flex-1 flex items-center gap-1.5 justify-end">
          <span className="text-brightblue-400 font-medium text-sm">$</span>
          <input type="number" value={formData.bidAmount} onChange={(e) => updateField('bidAmount', e.target.value)} disabled={isLoading} placeholder="0"
            className="w-36 px-2.5 py-1.5 border border-brightblue-300 rounded-lg text-sm text-brightblue-700 font-semibold bg-white outline-none focus:ring-2 focus:ring-brightblue-400 focus:border-brightblue-500 text-right disabled:opacity-50" />
        </div>
      </div>

      {(totalRevenue > 0 || totalHours > 0 || rawData?.components?.length > 0) && (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-grey-400 mb-2">
            Actuals from QuickBooks
            {selectedYear && selectedYear !== 'all' && <span className="ml-1.5 normal-case font-normal">· {periodLabel}</span>}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <PricingTile label="Avg / Month" value={fmt(avgRevenue)} sub={billedMonthCount ? `${billedMonthCount} mo.` : undefined} color={avgRevenue != null ? 'teal' : 'default'} />
            <PricingTile label="Total Revenue" value={fmt(totalRevenue)} sub={billedMonthCount ? `${billedMonthCount} months` : undefined} />
            <PricingTile label="Total Cost" value={fmt(totalCost) || '—'} />
            <PricingTile label="Actual Hours" value={fmtHrs(totalHours)} />
            <PricingTile label="Profit Margin" tooltip={TOOLTIPS.margin}
              value={profitMargin != null ? `${profitMargin.toFixed(1)}%` : '—'}
              sub={totalCost > 0 ? `Cost: ${fmt(totalCost)}` : undefined}
              color={marginColor}
            />
          </div>
          {showComparison && (
            <div className={`mt-2 px-3 py-2 rounded-lg border text-xs flex items-center justify-between ${comparisonAbove ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
              <span>{comparisonAbove ? `✓ ${comparisonLabel} ${fmt(comparisonDiff)} above bid${comparisonSuffix}` : `⚠ ${comparisonLabel} ${fmt(Math.abs(comparisonDiff))} below bid${comparisonSuffix}`}</span>
              <span className="font-semibold">{comparisonPct}% realized{comparisonSuffix}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── DocumentSection ───────────────────────────────────────────────────────────

const DocumentSection = ({
  formData, setFormData, isLoading, documents, loadingDocuments,
  onPreviewDocument, engagementId,
}) => {
  const [menuOpen, setMenuOpen]             = useState(false);
  const [showSelector, setShowSelector]     = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [extracting, setExtracting]         = useState(false);
  const [extractComplete, setExtractComplete] = useState(false);
  const [uploadedDocName, setUploadedDocName] = useState(null);
  const fileInputRef = useRef(null);
  const menuRef      = useRef(null);
 
  const uploadMutation  = useUploadDocument();
  const extractMutation = useExtractProposal();
 
  const linkedDoc = documents.find((d) => d.id === formData.documentId);
 
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);
 
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
 
    const displayName = file.name.replace(/\.[^/.]+$/, '');
    setUploadedDocName(displayName);
    setUploading(true);
 
    try {
      const res        = await uploadMutation.mutateAsync({ file, displayName, category: 'Proposal', engagementId });
      const uploadedDoc = res?.data || res;
 
      if (uploadedDoc?.id) {
        // Link to this engagement form
        setFormData((p) => ({ ...p, documentId: uploadedDoc.id }));
 
        // Auto-extract if PDF
        if (file.type === 'application/pdf') {
          setUploading(false);
          setExtracting(true);
          setExtractComplete(false);
          try {
            await extractMutation.mutateAsync({ documentId: uploadedDoc.id });
            setExtractComplete(true);
            await new Promise(r => setTimeout(r, 600));
          } catch (extractErr) {
            console.error('Auto-extract failed:', extractErr);
          } finally {
            setExtracting(false);
            setExtractComplete(false);
          }
        }
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadedDocName(null);
    }
  };
 
  // ── Loading states ──────────────────────────────────────────────────────────
  if (uploading) return (
    <div className="flex items-center gap-3 py-3 px-3 bg-grey-50 border border-grey-200 rounded-lg">
      <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
      <span className="text-xs text-grey-500">Uploading <span className="font-medium text-darkblue-500">{uploadedDocName}</span>...</span>
    </div>
  );
 
  if (extracting) return (
    <div className="flex items-center gap-3 py-3 px-3 bg-brand-50 border border-brand-200 rounded-lg">
      <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
      <span className="text-xs text-brand-600">Extracting data from document...</span>
    </div>
  );
 
  // ── No doc linked, no selector ────────────────────────────────────────────
  if (!linkedDoc && !showSelector) return (
    <div className="space-y-2">
      <div className="flex items-center justify-between py-2">
        <p className="text-xs text-grey-400">No document linked.</p>
        <div className="flex items-center gap-2">
          {/* Upload new document directly */}
          <div>
            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 text-xs font-medium text-darkblue-500 bg-darkblue-50 border border-darkblue-200 px-2.5 py-1 rounded-md hover:bg-darkblue-100 transition-colors">
              <DocumentArrowUpIcon className="w-3.5 h-3.5" />Upload
            </button>
          </div>
          {/* Or link existing */}
          <button type="button" onClick={() => setShowSelector(true)}
            className="text-xs font-medium text-darkblue-500 border border-grey-300 px-2.5 py-1 rounded-md hover:bg-grey-100 transition-colors">
            Link Existing
          </button>
        </div>
      </div>
    </div>
  );
 
  // ── Doc linked ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-2">
      {linkedDoc && (
        <div className="flex items-center gap-2 p-2.5 bg-darkblue-50 border border-darkblue-200 rounded-lg">
          <DocumentTextIcon className="w-4 h-4 text-darkblue-400 flex-shrink-0" />
          <span className="text-sm font-medium text-darkblue-500 flex-1 truncate">{linkedDoc.display_name}</span>
          {/* Extracted data chips */}
          {linkedDoc.client_name && (
            <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full hidden sm:inline">
              {linkedDoc.client_name}
            </span>
          )}
          {linkedDoc.bid_amount != null && (
            <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full hidden sm:inline">
              ${Number(linkedDoc.bid_amount).toLocaleString()}
            </span>
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            {onPreviewDocument && (
              <button type="button" onClick={() => onPreviewDocument(formData.documentId)}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-darkblue-500 border border-grey-300 rounded-md hover:bg-grey-100 transition-colors">
                <EyeIcon className="w-3.5 h-3.5" />Preview
              </button>
            )}
            <div className="relative" ref={menuRef}>
              <button type="button" onClick={() => setMenuOpen((v) => !v)}
                className="p-1.5 text-grey-400 border border-grey-300 rounded-md hover:bg-grey-100 transition-colors">
                <Cog6ToothIcon className="w-3.5 h-3.5" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-white border border-grey-200 rounded-lg shadow-lg py-1 text-xs">
                  <button type="button" onClick={() => { setMenuOpen(false); setShowSelector(true); }}
                    className="w-full px-3 py-2 text-left text-darkblue-500 hover:bg-grey-50 transition-colors">
                    Change document
                  </button>
                  <button type="button" onClick={() => { fileInputRef.current?.click(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-darkblue-500 hover:bg-grey-50 transition-colors flex items-center gap-2">
                    <DocumentArrowUpIcon className="w-3.5 h-3.5" />Upload new
                  </button>
                  <button type="button"
                    onClick={() => { setFormData((p) => ({ ...p, documentId: '' })); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-pink-500 hover:bg-pink-50 transition-colors">
                    Unlink document
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
 
      {/* Hidden file input for menu upload */}
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" />
 
      {linkedDoc && (
        <p className="text-xs text-grey-400 flex items-center gap-1 px-0.5">
          <SparklesIcon className="w-3.5 h-3.5 text-grey-300" />
          To extract fields, open <strong>Settings</strong> (gear icon in header).
        </p>
      )}
 
      {showSelector && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-darkblue-500">Select document</p>
            <button type="button" onClick={() => setShowSelector(false)} className="text-xs text-grey-400 hover:text-grey-600">Cancel</button>
          </div>
          {loadingDocuments ? <p className="text-xs text-grey-400">Loading documents...</p> : (
            <select value={formData.documentId || ''} disabled={isLoading}
              onChange={(e) => { setFormData((p) => ({ ...p, documentId: e.target.value })); setShowSelector(false); }}
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none">
              <option value="">Select a document...</option>
              {documents.map((doc) => <option key={doc.id} value={doc.id}>{doc.display_name} ({doc.category})</option>)}
            </select>
          )}
          <p className="text-xs text-grey-400">
            Don't see it? <a href="/documents" target="_blank" rel="noopener noreferrer" className="text-darkblue-500 hover:underline">Upload in Documents →</a>
          </p>
        </div>
      )}
    </div>
  );
};

// ── ActualsSection ────────────────────────────────────────────────────────────

const ActualsSection = ({ engagement, onViewDetails, onSync, isSyncing, selectedYear }) => {
  const engagementId = engagement?.id || engagement?.engagement_id;
  const isRetainer   = engagement?.engagement_type?.toLowerCase() === 'retainer';
  const { data: rawData, isLoading } = useBidEngagementComponents({ engagementId, enabled: !!engagementId });

  const allComponents = useMemo(() => normalizeComponents(rawData?.components), [rawData]);
  const components    = useMemo(() => applyYearFilter(allComponents, selectedYear), [allComponents, selectedYear]);
  const analysis      = useMemo(() => buildAnalysis(components, isRetainer), [components, isRetainer]);

  if (isLoading) return (
    <div className="flex items-center gap-2 py-4 text-sm text-grey-400">
      <ArrowPathIcon className="w-4 h-4 animate-spin text-darkblue-500" />Loading actuals...
    </div>
  );
  if (!analysis) return (
    <div className="py-5 text-center border border-dashed border-grey-200 rounded-lg">
      <UserGroupIcon className="w-7 h-7 text-grey-300 mx-auto mb-1.5" />
      <p className="text-sm text-grey-500">No actuals data{selectedYear && selectedYear !== 'all' ? ` from ${selectedYear} onwards` : ''}</p>
      <p className="text-xs text-grey-400 mt-0.5">Sync from QuickBooks or adjust the period in Settings</p>
    </div>
  );

  const monthHours = analysis.monthlyBreakdown.map((m) => m.hours);
  const footerMin  = monthHours.length ? Math.min(...monthHours) : 0;
  const footerMax  = monthHours.length ? Math.max(...monthHours) : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-grey-500">
          {selectedYear && selectedYear !== 'all' ? `${selectedYear}+` : 'All time'} · {analysis.monthCount} months
        </p>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={onSync} disabled={isSyncing} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-grey-300 text-darkblue-500 rounded-md hover:bg-grey-100 transition-colors disabled:opacity-50">
            <ArrowPathIcon className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />{isSyncing ? 'Syncing...' : 'Sync'}
          </button>
          <button type="button" onClick={onViewDetails} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-grey-300 text-darkblue-500 rounded-md hover:bg-grey-100 transition-colors">
            <ArrowsPointingOutIcon className="w-3.5 h-3.5" />View breakdown
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {analysis.services.map((svc) => (
          <div key={svc.service} className="border border-grey-200 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 bg-grey-50 border-b border-grey-200">
              <span className="text-xs font-bold text-darkblue-500 uppercase tracking-wide">{svc.service}</span>
              <span className="text-xs">{svc.totalHours.toFixed(1)} hrs total</span>
            </div>
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col /><col className="w-32" /><col className="w-36" /><col className="w-72" />
              </colgroup>
              <thead className="border-b border-grey-100">
                <tr className="bg-white">
                  <th className="px-3 py-1.5 text-left text-xs font-semibold text-grey-600">Role</th>
                  {isRetainer ? (
                    <>
                      <th className="px-3 py-1.5 text-center text-xs font-semibold text-grey-600"><span className="inline-flex items-center justify-end">Avg<Tooltip text={TOOLTIPS.average} /></span></th>
                      <th className="px-3 py-1.5 text-center text-xs font-semibold text-grey-600"><span className="inline-flex items-center justify-end">Range<Tooltip text={TOOLTIPS.range} /></span></th>
                    </>
                  ) : (
                    <th className="px-3 py-1.5 text-right text-xs font-semibold text-grey-600">Hours</th>
                  )}
                  <th className="px-3 py-1.5 text-left text-xs font-semibold text-grey-400 hidden sm:table-cell">Team</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-50">
                {svc.roles.map((r) => (
                  <tr key={`${svc.service}-${r.role}`} className="hover:bg-grey-50 transition-colors">
                    <td className="px-3 py-2 font-medium text-darkblue-500 text-xs">{getRoleDisplayName(r.role)}</td>
                    {isRetainer ? (
                      <>
                        <td className="px-3 py-2 text-center font-semibold text-darkblue-500 text-xs">{r.avgHours.toFixed(1)}</td>
                        <td className="px-3 py-2 text-center text-xs font-medium text-darkblue-500">
                          <span className="tabular-nums">{r.minHours.toFixed(1)}<span className="mx-1 text-grey-300">–</span>{r.maxHours.toFixed(1)}</span>
                        </td>
                      </>
                    ) : (
                      <td className="px-3 py-2 text-right font-semibold text-darkblue-500 text-xs">{r.totalHours.toFixed(1)}</td>
                    )}
                    <td className="px-3 py-2 hidden sm:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {r.employeeNames.slice(0, 2).map((name) => (
                          <span key={name} className="inline-block px-1.5 py-0.5 bg-grey-100 text-grey-600 text-xs rounded">{name.split(' ')[0]}</span>
                        ))}
                        {r.employeeNames.length > 2 && (
                          <span className="inline-block px-1.5 py-0.5 bg-grey-100 text-grey-400 text-xs rounded">+{r.employeeNames.length - 2}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-3 py-2 bg-grey-50 border border-grey-200 rounded-lg text-xs font-bold text-darkblue-500">
        <span>Total</span>
        <div className="flex items-center gap-4">
          {isRetainer ? (
            <>
              <span>{(analysis.totalHours / analysis.monthCount).toFixed(1)} avg/mo</span>
              <span className="text-xs font-normal tabular-nums">{footerMin.toFixed(1)}<span className="mx-1 text-grey-300">–</span>{footerMax.toFixed(1)}</span>
            </>
          ) : (
            <span>{analysis.totalHours.toFixed(1)} hrs</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── ActualsDetailModal ────────────────────────────────────────────────────────

export const ActualsDetailModal = ({ isOpen, onClose, engagement, selectedYear }) => {
  const engagementId = engagement?.id || engagement?.engagement_id;
  const isRetainer   = engagement?.engagement_type?.toLowerCase() === 'retainer';
  const { data: rawData, isLoading } = useBidEngagementComponents({ engagementId, enabled: isOpen && !!engagementId });

  const allComponents     = useMemo(() => normalizeComponents(rawData?.components), [rawData]);
  const allMonthlyRevenue = useMemo(() => rawData?.monthly_revenue || [], [rawData]);
  const components        = useMemo(() => applyYearFilter(allComponents, selectedYear), [allComponents, selectedYear]);
  const monthlyRevenue    = useMemo(() => applyYearFilter(allMonthlyRevenue, selectedYear), [allMonthlyRevenue, selectedYear]);
  const analysis          = useMemo(() => buildAnalysis(components, isRetainer), [components, isRetainer]);

  const totalRevenue     = useMemo(() => monthlyRevenue.reduce((s, r) => s + (r.revenue || 0), 0), [monthlyRevenue]);
  const billedMonthCount = useMemo(() => monthlyRevenue.filter((r) => (r.revenue || 0) > 0).length, [monthlyRevenue]);
  const revenueByMonth   = useMemo(() => {
    const map = {};
    monthlyRevenue.forEach((r) => { const k = r.month?.slice(0, 7); map[k] = (map[k] || 0) + (r.revenue || 0); });
    return map;
  }, [monthlyRevenue]);
  const totalCost  = analysis?.totalCost  ?? 0;
  const totalHours = analysis?.totalHours ?? 0;
  const margin     = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : null;
  const periodLabel = selectedYear && selectedYear !== 'all' ? `${selectedYear}+` : 'All time';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-grey-200 flex-shrink-0">
          <div>
            <h3 className="text-sm font-bold text-darkblue-500">Actuals Breakdown</h3>
            <p className="text-xs text-grey-500 mt-0.5">{engagement?.engagement_name} · {isRetainer ? 'Monthly averages' : 'Full project totals'} · {periodLabel}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors"><XMarkIcon className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading && <div className="flex items-center justify-center py-10 text-sm text-grey-400"><ArrowPathIcon className="w-4 h-4 animate-spin mr-2 text-darkblue-500" />Loading...</div>}
          {!isLoading && !analysis && (
            <div className="text-center py-8"><UserGroupIcon className="w-8 h-8 text-grey-300 mx-auto mb-2" /><p className="text-sm text-grey-500">No actuals data{selectedYear && selectedYear !== 'all' ? ` from ${selectedYear} onwards` : ''}</p></div>
          )}
          {!isLoading && analysis && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <PricingTile label="Total Revenue" value={fmt(totalRevenue)} sub={billedMonthCount ? `${billedMonthCount} billed mo.` : undefined} color="teal" />
                <PricingTile label="Avg / Month" value={billedMonthCount > 0 ? fmt(totalRevenue / billedMonthCount) : '—'} color="teal" />
                <PricingTile label="Total Hours" value={fmtHrs(totalHours)} />
                <PricingTile label="Profit Margin" tooltip={TOOLTIPS.margin} value={margin != null ? `${margin.toFixed(1)}%` : '—'} sub={totalCost > 0 ? `Cost: ${fmt(totalCost)}` : undefined} color={margin == null ? 'default' : margin >= 20 ? 'teal' : margin >= 10 ? 'yellow' : 'red'} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-grey-400 mb-2">Monthly Breakdown — {analysis.monthCount} months · {periodLabel}</p>
                <div className="space-y-1.5">
                  {analysis.monthlyBreakdown.map((m) => {
                    const monthRevenue = revenueByMonth[m.month?.slice(0, 7)] ?? null;
                    return (
                      <details key={m.month} className="border border-grey-200 rounded-lg overflow-hidden">
                        <summary className="flex items-center justify-between px-3 py-2.5 bg-grey-50 cursor-pointer hover:bg-grey-100 transition-colors list-none">
                          <span className="text-sm font-semibold text-darkblue-500">{new Date(m.month + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</span>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="font-semibold text-teal-700">{monthRevenue != null ? fmt(monthRevenue) : '—'}</span>
                            <span className="text-grey-500">{m.hours.toFixed(1)} hrs</span>
                            <span className="text-grey-300">▼</span>
                          </div>
                        </summary>
                        <div className="divide-y divide-grey-100">
                          {m.services.map((svc) => (
                            <div key={svc.name}>
                              <div className="flex items-center justify-between px-3 py-2 bg-white border-t border-grey-100">
                                <span className="text-xs font-semibold text-darkblue-400 uppercase tracking-wide">{svc.name}</span>
                                <div className="flex items-center gap-3 text-xs text-grey-600"><span>{svc.hours.toFixed(1)} hrs</span><span>{fmt(svc.cost)}</span></div>
                              </div>
                              <table className="w-full text-xs">
                                <tbody className="divide-y divide-grey-50">
                                  {svc.employees.map((emp, i) => (
                                    <tr key={i} className="hover:bg-grey-50">
                                      <td className="px-6 py-2 font-medium text-darkblue-500">{emp.employee_name || '—'}</td>
                                      <td className="px-3 py-2 text-grey-500">{getRoleDisplayName(emp.employee_role) || '—'}</td>
                                      <td className="px-3 py-2 text-right text-darkblue-500">{(emp.hours || 0).toFixed(1)}</td>
                                      <td className="px-3 py-2 text-right text-grey-500">{fmt(emp.cost) || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between px-3 py-2 bg-grey-50 border-t border-grey-200 text-xs font-semibold text-darkblue-500">
                          <span>Month Total</span>
                          <div className="flex items-center gap-6"><span>{m.hours.toFixed(1)} hrs</span><span>{fmt(m.cost)}</span></div>
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-grey-200 flex-shrink-0 bg-grey-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium border border-grey-300 text-darkblue-500 rounded-lg hover:bg-grey-100 transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────

const EngagementModal = ({
  isOpen, isEdit = false, formData, setFormData, onSubmit, onClose,
  isLoading = false, validationErrors = [], existingEngagement = null,
  documents = [], loadingDocuments = false, onPreviewDocument,
  industries = DEFAULT_INDUSTRIES,
}) => {
  const [activeSection, setActiveSection]        = useState('overview');
  const [showActualsDetail, setShowActualsDetail] = useState(false);
  const [showSettings, setShowSettings]          = useState(false);

  // const currentYear = String(new Date().getFullYear());
  const currentYear = "all"
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const scrollRef   = useRef(null);
  const sectionRefs = useRef({});
  const isScrolling = useRef(false);
  const queryClient    = useQueryClient();
  const syncActuals    = useSyncActuals();
  const updateEngagement = useUpdateEngagement();
  const updateField    = useCallback((field, value) => setFormData((p) => ({ ...p, [field]: value })), [setFormData]);
  const engagementId   = existingEngagement?.id || existingEngagement?.engagement_id;

  const { data: rawData } = useBidEngagementComponents({ engagementId, enabled: !!engagementId });
  const availableYears = useMemo(() => {
    const years = new Set();
    (rawData?.components || []).forEach((c) => { if (c.month) years.add(c.month.slice(0, 4)); });
    (rawData?.monthly_revenue || []).forEach((r) => { if (r.month) years.add(r.month.slice(0, 4)); });
    return [...years].sort();
  }, [rawData]);

  // Reset year filter when modal opens
  useEffect(() => { if (isOpen) setSelectedYear(currentYear); }, [isOpen]);

  // FIX 10: receives already-merged formData from SettingsModal
  const handleApplyAndSave = useCallback(async (mergedFormData) => {
    if (!isEdit || !engagementId) return;
    await updateEngagement.mutateAsync({ engagementId, updates: formDataToPayload(mergedFormData) });
  }, [isEdit, engagementId, updateEngagement]);

  const visibleSections = SECTIONS.filter((s) => {
    if (s.id === 'document' && documents.length === 0) return false;
    if (s.id === 'actuals'  && (!isEdit || !engagementId)) return false;
    return true;
  });

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const onScroll = () => {
      if (isScrolling.current) return;
      const threshold = container.getBoundingClientRect().top + 60;
      let current = visibleSections[0]?.id || 'overview';
      for (const { id } of visibleSections) {
        const el = sectionRefs.current[id];
        if (el && el.getBoundingClientRect().top <= threshold) current = id;
      }
      setActiveSection(current);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [isOpen]);

  const handleNavClick = useCallback((sectionId) => {
    const el = sectionRefs.current[sectionId];
    const container = scrollRef.current;
    if (!el || !container) return;
    isScrolling.current = true;
    setActiveSection(sectionId);
    const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
    container.scrollTo({ top: offset, behavior: 'smooth' });
    setTimeout(() => { isScrolling.current = false; }, 700);
  }, []);

  const setSectionRef = (id) => (el) => { sectionRefs.current[id] = el; };

  const handleSyncActuals = async () => {
    if (!engagementId) return;
    try {
      await syncActuals.mutateAsync(engagementId);
      await queryClient.invalidateQueries({ queryKey: ['bid-engagement-components', engagementId] });
    } catch { /* logged in hook */ }
  };

  const canSubmit = !isLoading && formData.engagementName?.trim();

  // ── FIX 11: Title node passed to common Modal ─────────────────────────────
  // The common Modal renders `title` inside its header alongside the close button.
  // We inject the settings gear into the title node so it sits in the header row
  // without needing to modify Modal.jsx.
  const modalTitle = (
    <div className="flex items-center gap-2 flex-wrap min-w-0 w-full">
      <span className="text-darkblue-500 font-semibold truncate flex-1">
        {isEdit && formData.engagementName ? formData.engagementName : (isEdit ? 'Edit Engagement' : 'New Engagement')}
      </span>
      {isEdit && formData.bidStatus && <StatusBadge status={formData.bidStatus} />}
      {isEdit && engagementId && (
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          title="Engagement settings"
          className="flex-shrink-0 p-1.5 text-grey-400 hover:text-darkblue-500 hover:bg-grey-100 rounded-lg transition-colors"
        >
          <Cog6ToothIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  const footer = (
    <div className="flex gap-2 w-full">
      <button onClick={onClose} disabled={isLoading}
        className="flex-1 px-4 py-2 text-sm font-semibold border border-grey-300 text-darkblue-500 rounded-lg hover:bg-grey-50 transition-colors disabled:opacity-50">
        Cancel
      </button>
      <button onClick={onSubmit} disabled={!canSubmit}
        className="flex-1 px-4 py-2 text-sm font-semibold bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        {isLoading && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
        {isEdit ? 'Save Changes' : 'Create Engagement'}
      </button>
    </div>
  );

  return (
    <>
      {/* FIX 11: Use common Modal component
          - bodyClassName overrides MODAL_THEME.body.default ('p-6 overflow-y-auto flex-1')
            with p-0 + flex column so the scrollspy nav + inner scroll div work correctly.
          - The common Modal footer uses MODAL_THEME.footer.default which is already
            flex justify-end — we pass w-full on our inner div to fill it.
      */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle}
        subtitle={isEdit ? 'Edit Engagement' : 'New Engagement'}
        size="xl"
        footer={footer}
        bodyClassName="flex-1 min-h-0 flex flex-col overflow-hidden p-0"
      >
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm font-semibold text-darkblue-500">{isEdit ? 'Updating...' : 'Creating...'}</p>
            </div>
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className="mx-4 mt-2 p-2.5 bg-pink-50 border border-pink-200 rounded-lg flex-shrink-0">
            <p className="text-xs font-semibold text-pink-700 mb-1">Please fix the following:</p>
            <ul className="list-disc list-inside text-xs text-pink-600 space-y-0.5">
              {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        <ScrollspyNav
          sections={visibleSections}
          activeSection={activeSection}
          onClickSection={handleNavClick}
        />

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 pb-6 space-y-5">
          <div ref={setSectionRef('overview')} className="pt-4">
            <OverviewSection formData={formData} updateField={updateField} isLoading={isLoading} industries={industries} engagementId={engagementId} />
          </div>
          <div ref={setSectionRef('pricing')}>
            <SectionDivider title="Pricing" />
            <PricingSection formData={formData} existingEngagement={existingEngagement} isLoading={isLoading} updateField={updateField} selectedYear={selectedYear} />
          </div>
          {visibleSections.some((s) => s.id === 'document') && (
            <div ref={setSectionRef('document')}>
              <SectionDivider title="Document" />
              <DocumentSection formData={formData} setFormData={setFormData} isLoading={isLoading} documents={documents} loadingDocuments={loadingDocuments} onPreviewDocument={onPreviewDocument} />
            </div>
          )}
          {isEdit && engagementId && (
            <div ref={setSectionRef('actuals')}>
              <SectionDivider title="Actuals" />
              <ActualsSection engagement={existingEngagement} onViewDetails={() => setShowActualsDetail(true)} onSync={handleSyncActuals} isSyncing={syncActuals.isPending} selectedYear={selectedYear} />
            </div>
          )}
        </div>
      </Modal>

      {/* Settings sub-modal — z-[70] sits above the engagement modal z-50 */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        availableYears={availableYears}
        formData={formData}
        setFormData={setFormData}
        isEdit={isEdit}
        onApplyAndSave={handleApplyAndSave}
        documents={documents}
      />

      <ActualsDetailModal
        isOpen={showActualsDetail}
        onClose={() => setShowActualsDetail(false)}
        engagement={existingEngagement}
        selectedYear={selectedYear}
      />
    </>
  );
};

export default EngagementModal;